import React, { FC, useEffect, useMemo, useState } from "react";
import { Controller, Form, useForm } from "react-hook-form";
import {
  ApplicationFeature,
  FormQuestionDto,
  UserRole,
} from "@green0meter/api-client";
import FeatherIcon from "feather-icons-react";
import { useTranslation } from "react-i18next";
import { Autocomplete } from "@mui/material";

import Drawer from "@components/common/Drawer";
import {
  useGetCurrentCompanyInfo,
  useGetEmissionFactors,
  useUpdateEmissionFactorPair,
} from "@api";
import { useUserStore } from "@store/user";
import {
  Button,
  FormControlLabel,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@components/base";
import { emissionFactorTypeOptions } from "@components/admin/emissionFactors";
import { hasFeature } from "@helpers";

interface EmissionFactor {
  question: FormQuestionDto;
}

type EmissionFactorForm = {
  emissionFactorType: "greenometer" | "custom";
  emissionFactorId: string | undefined;
};

const EmissionFactor: FC<EmissionFactor> = ({ question }) => {
  const { t } = useTranslation();
  const [emissionFactorOpen, setEmissionFactorOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const { control, reset, watch } = useForm<EmissionFactorForm>({
    defaultValues: {
      emissionFactorType: "greenometer",
      emissionFactorId: "none",
    },
  });
  const { user } = useUserStore();
  const { emissionFactors } = useGetEmissionFactors(user);
  const { updateEmissionFactorPairAsync } = useUpdateEmissionFactorPair(user);
  const { currentCompanyInfo } = useGetCurrentCompanyInfo(user);
  const emissionFactorType = watch("emissionFactorType");

  const handleOpenEmissionFactor = (): void => {
    setEmissionFactorOpen(true);
  };

  const handleCloseEmissionFactor = (): void => {
    setEmissionFactorOpen(false);
  };

  const greenometerEmissionFactors = useMemo(() => {
    return emissionFactors?.filter((factor) => !factor.companyId);
  }, [emissionFactors]);

  const customEmissionFactors = useMemo(() => {
    return emissionFactors?.filter((factor) => factor.companyId);
  }, [emissionFactors]);

  const emissionFactor = useMemo(() => {
    const customEmissionFactor = emissionFactors?.find((factor) =>
      factor.pairs.some(
        (pair) => pair.questionCode === question.code && factor.companyId !== null,
      ),
    );

    if (customEmissionFactor) {
      return customEmissionFactor;
    }

    return emissionFactors?.find((factor) =>
      factor.pairs.some((pair) => pair.questionCode === question.code),
    );
  }, [emissionFactors, question.code]);

  const handleUpdateEmissionFactor = async (): Promise<void> => {
    const emissionFactorId = watch("emissionFactorId");

    if (emissionFactorId !== emissionFactor?.id) {
      const currentPairId = emissionFactor
        ? emissionFactor.pairs.find((pair) => pair.questionCode === question.code)
            ?.id
        : undefined;

      await updateEmissionFactorPairAsync({
        emissionFactorId: emissionFactorId || "",
        questionCode: question.code,
        currentPairId: currentPairId,
      });
    }

    handleCloseEmissionFactor();
  };

  useEffect(() => {
    reset({
      emissionFactorType: emissionFactor?.companyId ? "custom" : "greenometer",
      emissionFactorId: emissionFactor?.id || "none",
    });
  }, [emissionFactor, reset]);

  return hasFeature(
    currentCompanyInfo?.companyFeatureFunctionalities ?? [],
    ApplicationFeature.EF_MANAGEMENT,
  ) || user?.role === UserRole.Admin ? (
    <>
      <Typography
        variant="body2"
        color={emissionFactor?.companyId ? "primary" : "success"}
      >
        {emissionFactor?.factor}
      </Typography>
      <Tooltip title={t("multistep.editor.emissionFactor")} placement="top">
        <IconButton
          onClick={handleOpenEmissionFactor}
          color={
            emissionFactor
              ? emissionFactor.companyId
                ? "primary"
                : "success"
              : "default"
          }
        >
          <FeatherIcon icon="cloud-rain" size={16} />
        </IconButton>
      </Tooltip>
      <Drawer
        open={emissionFactorOpen}
        onClose={handleCloseEmissionFactor}
        content={
          <Form control={control}>
            <Stack gap={3}>
              <Typography variant="subtitle1">
                {t("multistep.editor.emissionFactor")}
              </Typography>
              <Controller
                name="emissionFactorType"
                control={control}
                render={({ field }) => (
                  <RadioGroup row {...field} value={field.value}>
                    <FormControlLabel
                      control={<Radio value="greenometer" />}
                      label={t("multistep.editor.emissionFactorType.greenometer")}
                    />
                    <FormControlLabel
                      control={<Radio value="custom" />}
                      label={t("multistep.editor.emissionFactorType.custom")}
                    />
                  </RadioGroup>
                )}
              />
              {emissionFactorType === "greenometer" && (
                <Controller
                  name="emissionFactorId"
                  control={control}
                  render={({ field: { onChange, onBlur, value }, fieldState }) => {
                    const currentValue =
                      greenometerEmissionFactors?.find(
                        (factor) => factor.id === value,
                      ) || null;

                    return (
                      <Autocomplete
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            onChange={(event) => setSearchTerm(event.target.value)}
                            placeholder={t(
                              "multistep.editor.emissionFactorSearch.greenometerPlaceholder",
                            )}
                            onBlur={onBlur}
                            error={!!fieldState.error}
                          />
                        )}
                        options={greenometerEmissionFactors ?? []}
                        getOptionLabel={(option) =>
                          `${option.factor.toString()} ${option.unit.abbreviation}`
                        }
                        isOptionEqualToValue={(option, value) =>
                          option.id === value.id
                        }
                        getOptionKey={(option) => option.id}
                        onChange={(_, value) => {
                          onChange(value ? value.id : "none");
                        }}
                        value={currentValue}
                        noOptionsText={t(
                          "multistep.editor.emissionFactorSearch.greenometerNoOptions",
                        )}
                        inputValue={
                          currentValue
                            ? `${currentValue.factor.toString()} ${currentValue.unit.abbreviation}`
                            : searchTerm
                        }
                      />
                    );
                  }}
                />
              )}
              {emissionFactorType === "custom" && (
                <Controller
                  name="emissionFactorId"
                  control={control}
                  render={({ field, fieldState }) => (
                    <Select
                      variant="filled"
                      displayEmpty
                      error={!!fieldState.error}
                      {...field}
                    >
                      <MenuItem value="none">{t("common.none")}</MenuItem>
                      {customEmissionFactors?.map((factor) => {
                        const typeOption = emissionFactorTypeOptions.find(
                          (option) => option.value === factor.type,
                        );

                        return (
                          <MenuItem value={factor.id} key={factor.id}>
                            {`${typeOption ? t(`emissionFactorEditor.${typeOption.labelKey}`) : factor.type} - ${factor.factor} ${factor.unit.abbreviation}`}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  )}
                />
              )}
              <Stack direction="row" justifyContent="space-between" gap={2}>
                <Button onClick={handleCloseEmissionFactor} variant="outlined">
                  {t("common.close")}
                </Button>
                <Button variant="contained" onClick={handleUpdateEmissionFactor}>
                  {t("common.saveChanges")}
                </Button>
              </Stack>
            </Stack>
          </Form>
        }
      />
    </>
  ) : null;
};

export default EmissionFactor;
